import RichText from "@/components/RichText";
import { CONTAINER, PANEL_BG, SERIF, lines } from "../tokens";

// Section « Tarifs » du handoff : bande pleine largeur sombre, en-tête sur
// deux colonnes, puis une carte par plan. Un plan `highlight` prend le
// traitement « Sur-mesure » de la maquette (carte sombre, bandeau lime en
// haut, bouton plein) ; les autres sont les cartes blanches à bouton
// contour. Les prix sont des TEXTES libres, jamais des produits du
// catalogue : le handoff affiche des prix fermes, écrits.
export default function PricingPlansSection({ props = {}, blocks = [] }) {
  const plans = (blocks || []).filter((b) => b.type === "plan_card" && b.enabled !== false);
  const chips = lines(props.chips);

  return (
    <section id="tarifs" className="bg-ink text-cream pt-[86px] pb-[94px] scroll-mt-24">
      <div className={CONTAINER}>
        <div className="grid md:grid-cols-2 gap-9 items-start">
          <RichText
            as="h2"
            className="font-display font-bold text-[clamp(32px,4vw,52px)] leading-[1.02] tracking-[-0.035em] max-w-[540px] [text-wrap:pretty]"
            html={props.heading}
          />
          <div>
            <RichText
              as="p"
              preserveParagraphs
              className="text-[16.5px] leading-[1.6] text-cream/[0.68] max-w-[450px]"
              html={props.subheading}
            />
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2.5 mt-5">
                {chips.map((c) => (
                  <span key={c} className="text-[13.5px] bg-cream/10 rounded-[7px] py-2 px-3 text-cream/[0.78]">
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-[18px] mt-14 items-stretch [grid-template-columns:repeat(auto-fit,minmax(272px,1fr))]">
          {plans.map((b) => {
            const p = b.props || {};
            const highlight = String(p.highlight) === "true";
            const features = lines(p.features);
            const excluded = lines(p.excluded);

            const header = (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <RichText
                    as="h3"
                    className="font-display font-bold text-[25px] tracking-[-0.02em]"
                    html={p.name}
                  />
                  {p.tag && (
                    <span
                      className={`font-display text-[12px] tracking-[0.08em] uppercase ${
                        highlight ? "text-cream/[0.5]" : "text-ink/[0.6]"
                      }`}
                    >
                      {p.tag}
                    </span>
                  )}
                </div>
                {p.baseline && (
                  <p
                    className={`mt-2.5 text-[14.5px] leading-[1.5] ${
                      highlight ? "text-cream/[0.68]" : "text-ink/[0.58]"
                    }`}
                  >
                    {p.baseline}
                  </p>
                )}
                <div className="flex items-baseline gap-2 mt-[26px] flex-wrap">
                  {p.price_prefix && (
                    <span className={`text-[14px] ${highlight ? "text-cream/[0.65]" : "text-ink/[0.62]"}`}>
                      {p.price_prefix}
                    </span>
                  )}
                  <span className="text-[52px] leading-none tracking-[-0.01em]" style={{ fontFamily: SERIF }}>
                    {p.price}
                  </span>
                  {p.price_note && (
                    <span className={`text-[13px] ${highlight ? "text-cream/[0.62]" : "text-ink/[0.62]"}`}>
                      {p.price_note}
                    </span>
                  )}
                </div>
                {p.cta_label && (
                  <a
                    href={p.cta_url || "#contact"}
                    className={`mt-5 flex items-center justify-between rounded-btn py-[13px] px-4 font-display font-medium text-[15px] transition-colors duration-150 ${
                      highlight
                        ? "bg-blush border-[1.5px] border-blush text-ink hover:opacity-90"
                        : "border-[1.5px] border-ink text-ink hover:bg-ink hover:text-cream"
                    }`}
                  >
                    {p.cta_label} <span aria-hidden="true">→</span>
                  </a>
                )}
                {(features.length > 0 || excluded.length > 0) && (
                  <div
                    className={`mt-[26px] pt-[22px] border-t flex flex-col gap-3 text-[14.5px] leading-[1.45] ${
                      highlight ? "border-cream/[0.14]" : "border-ink/10"
                    }`}
                  >
                    {features.map((f) => (
                      <div key={f} className="flex gap-2.5">
                        <span className={highlight ? "text-blush" : "text-gold"} aria-hidden="true">✓</span>
                        <span>{f}</span>
                      </div>
                    ))}
                    {excluded.map((f) => (
                      <div
                        key={f}
                        className={`flex gap-2.5 ${highlight ? "text-cream/[0.5]" : "text-ink/[0.63]"}`}
                      >
                        <span aria-hidden="true">–</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );

            if (highlight) {
              return (
                <div
                  key={b.id}
                  data-block-key={b.id}
                  className="text-cream border-control border-cream/[0.14] rounded-card flex flex-col shadow-[0_22px_48px_-26px_rgba(0,0,0,0.7)] overflow-hidden"
                  style={PANEL_BG}
                >
                  {p.ribbon_label && (
                    <div className="bg-blush text-ink font-display font-semibold text-[12px] tracking-[0.1em] uppercase py-[11px] px-[26px]">
                      {p.ribbon_label}
                    </div>
                  )}
                  <div className="p-7 pt-[28px] flex flex-col flex-1">{header}</div>
                </div>
              );
            }

            return (
              <div
                key={b.id}
                data-block-key={b.id}
                className="bg-white text-ink rounded-card pt-[30px] px-[26px] pb-[26px] flex flex-col shadow-[0_1px_2px_rgba(11,18,32,0.05),0_18px_40px_-26px_rgba(0,0,0,0.55)]"
              >
                {header}
              </div>
            );
          })}
        </div>

        {(props.footnote || props.footnote_link_label) && (
          <p className="mt-10 text-[14px] text-cream/[0.6]">
            {props.footnote}{" "}
            {props.footnote_link_label && (
              <a
                href={props.footnote_link_url || "#contact"}
                className="text-blush border-b border-blush/40 hover:border-blush transition-colors"
              >
                {props.footnote_link_label}
              </a>
            )}
          </p>
        )}
      </div>
    </section>
  );
}
